import React, { useEffect, useMemo, useState } from 'react';
import { FaCheck, FaFilter, FaPlus, FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  createItemFromTemplate,
  createNewItem,
  dupplicateItem,
  getSearch,
} from '../../services/item';
import { setOpenModales } from '../../store/global';
import { saveAll } from '../../store/items';
import { upsertChild } from '../../store/navigation';
import { addLink } from '../../utils/item';
import {
  filterItemsByTemplateMode,
  getTemplateCreatedItemLabel,
  shouldCreateFromTemplateSelection,
} from '../../utils/templateSearch';
import { BtnSimple } from '../form/btn.Simple';
import { Checkbox } from '../form/checkbox';
import { InputSimple } from '../form/input.Simple';
import { ModalGlobal } from '../modal/modal.Global';
import { useSyncedCampaignFilter } from '../../hooks/useSyncedCampaignFilter';
import { LinkSelectedTemplate } from './link.SelectedTemplate';

export const LinkNew = props => {
  const {
    itemId,
    mode,
    modalId,
    title,
    onHandleExistingItem,
    onHandleCreatedItem,
  } = props;

  const item = useSelector(state => state.items[itemId]);
  const items = useSelector(state => state.items);
  const listCampaignsId = useSelector(state => state.global.listCampaignsId);
  const dispatch = useDispatch();

  const [dupplicateCurrent, setDupplicateCurrent] = useState(false);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [searchedItemsList, setSearchedItemsList] = useState([]);
  const [searchedItem, setSearchedItem] = useState('');
  const [displayLabel, setDisplayLabel] = useState('');
  const [showCampaignFilter, setShowCampaignFilter] = useState(false);
  const [templatesOnly, setTemplatesOnly] = useState(false);
  const [selectedCampaignIds, setSelectedCampaignIds] =
    useSyncedCampaignFilter(listCampaignsId, 'item-search-campaigns');
  const campaignOptions = useMemo(() => {
    if (!Array.isArray(listCampaignsId)) {
      return [];
    }
    return listCampaignsId
      .filter(Boolean)
      .map(id => ({ id, label: items?.[id]?.label || 'Dossier' }));
  }, [listCampaignsId, items]);
  const searchIn = {
    type: false,
    universe: true,
    world: true,
    campaign: false,
  };

  const search = async value => {
    if (Array.isArray(selectedCampaignIds) && selectedCampaignIds.length === 0) {
      setSearchedItemsList([]);
      return;
    }
    const res = await getSearch(value, item, searchIn, selectedCampaignIds, {
      isTemplate: templatesOnly,
    });
    if (Array.isArray(res)) {
      setSearchedItemsList(res);
    } else {
      setSearchedItemsList([]);
    }
  };

  const openModalId = modalId || 'addLink' + itemId;
  const visibleSearchedItemsList = useMemo(
    () => filterItemsByTemplateMode(searchedItemsList, templatesOnly),
    [searchedItemsList, templatesOnly],
  );

  const handleCreate = async () => {
    const folderCallback =
      mode === 'folder'
        ? createdItem => {
            if (createdItem?._id) {
              dispatch(saveAll({ item: createdItem, log: 'link-new-folder' }));
              dispatch(
                upsertChild({ parentId: itemId, childId: createdItem._id })
              );
            }
          }
        : null;

    if (searchedItem) {
      if (shouldCreateFromTemplateSelection(searchedItem)) {
        await createItemFromTemplate(searchedItem, item, {
          label: newItemLabel || getTemplateCreatedItemLabel(searchedItem),
          callback: createdItem => {
            if (typeof onHandleCreatedItem === 'function') {
              onHandleCreatedItem(createdItem, searchedItem);
              return;
            }
            const defaultCb = folderCallback || (!mode && addLink);
            if (typeof defaultCb === 'function') {
              defaultCb(createdItem, item);
            }
          },
        });
      } else if (dupplicateCurrent) {
        await dupplicateItem(
          {
            ...searchedItem,
            label: searchedItem.label + '[copie]',
            callback: createdItem => {
              if (typeof onHandleCreatedItem === 'function') {
                onHandleCreatedItem(createdItem, searchedItem);
                return;
              }
              const defaultCb = folderCallback || (!mode && addLink);
              if (typeof defaultCb === 'function') {
                defaultCb(createdItem, item);
              }
            },
          },
          item
        );
      } else if (typeof onHandleExistingItem === 'function') {
        await onHandleExistingItem(searchedItem);
      } else {
        await addLink(item, searchedItem, () => {
          resetForm();
        });
      }
    } else if (newItemLabel !== '') {
      await createNewItem(
        {
          label: newItemLabel,
          callback: createdItem => {
            if (createdItem?._id) {
              dispatch(saveAll({ item: createdItem, log: 'link-new-create' }));
            }
            if (typeof onHandleCreatedItem === 'function') {
              onHandleCreatedItem(createdItem);
              return;
            }
            const defaultCb = folderCallback || (!mode && addLink);
            if (typeof defaultCb === 'function') {
              defaultCb(createdItem, item);
            }
          },
        },
        item
      );
    }
    resetForm();
    dispatch(setOpenModales({ id: openModalId, open: false }));
  };

  const resetForm = () => {
    setNewItemLabel('');
    setSearchedItemsList([]);
    setSearchedItem('');
    mode !== 'folder' && setDupplicateCurrent(false);
  };

  const clearSelectedTemplate = () => {
    setSearchedItem('');
    setSearchedItemsList([]);
    setDupplicateCurrent(false);
  };

  useEffect(() => {
    if (searchedItem) {
      setDisplayLabel(
        shouldCreateFromTemplateSelection(searchedItem)
          ? newItemLabel || getTemplateCreatedItemLabel(searchedItem)
          : searchedItem.label +
          (dupplicateCurrent
            ? ' [copie]'
            : '')
      );
    } else {
      setDisplayLabel(newItemLabel);
    }
  }, [searchedItem, dupplicateCurrent, newItemLabel]);

  // en mode folder, on ne peut que duppliquer un item sélectionné
  useEffect(() => {
    if (mode === 'folder') {
      setDupplicateCurrent(true);
    }
  }, [mode]);

  useEffect(() => {
    if (templatesOnly && searchedItem && !shouldCreateFromTemplateSelection(searchedItem)) {
      setSearchedItem('');
      setNewItemLabel('');
      setDupplicateCurrent(false);
    }
  }, [templatesOnly, searchedItem]);

  return (
    <ModalGlobal
      idToOpen={openModalId}
      title={title || `Ajouter un lien à ${item?.label}`}
    >
      <div className='w-full flex flex-wrap items-center '>
        <div className='w-full flex items-center gap-05'>
          <InputSimple
            className='flex-1 m-0'
            resetValue={displayLabel}
            placeholder={"nom de l'item"}
            onChange={e => {
              const value = e.target.value;
              setNewItemLabel(value);
              if (
                searchedItem &&
                !shouldCreateFromTemplateSelection(searchedItem) &&
                value !== searchedItem.label
              ) {
                setSearchedItem('');
                mode !== 'folder' && setDupplicateCurrent(false);
              }
              search(value);
            }}
            onBlur={() => null}
          />
          {campaignOptions.length > 0 && (
            <button
              type='button'
              className={`shrink-0 transition hover:opacity-70 ${
                showCampaignFilter ||
                selectedCampaignIds.length !== campaignOptions.length
                  ? 'text-secondary'
                  : 'text-contrast text-opacity-70'
              }`}
              title='Filtrer les dossiers'
              onClick={() => setShowCampaignFilter(prev => !prev)}
            >
              <FaFilter />
            </button>
          )}
        </div>
        {campaignOptions.length > 0 && showCampaignFilter && (
          <div className='w-full mt-03 max-h-8 overflow-auto bg-main bg-opacity-40 rounded p-04 border border-main-d border-opacity-40'>
            {campaignOptions.map(option => {
              const checked = selectedCampaignIds.includes(option.id);
              return (
                <div
                  key={option.id}
                  className='py-02'
                >
                  <Checkbox
                    checked={checked}
                    size='sm'
                    label={option.label}
                    labelClassName='text-xs'
                    onChange={() =>
                      setSelectedCampaignIds(prev =>
                        prev.includes(option.id)
                          ? prev.filter(id => id !== option.id)
                          : [...prev, option.id],
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        )}
        <div className='w-full mt-05 flex justify-end'>
          <Checkbox
            checked={templatesOnly}
            size='sm'
            label='Afficher uniquement les templates'
            labelClassName='text-xs'
            onChange={checked => setTemplatesOnly(checked)}
          />
        </div>
        {shouldCreateFromTemplateSelection(searchedItem) && (
          <LinkSelectedTemplate
            item={searchedItem}
            onClear={clearSelectedTemplate}
          />
        )}
        <div className='w-full flex'>
          <div className='w-1/4'></div>
          <div>
            {visibleSearchedItemsList.map(searchItem => (
              <div
                key={searchItem._id}
                className='cursor-pointer flex gap-1 items-center'
                onClick={() => {
                  setSearchedItem(searchItem);
                  setNewItemLabel(
                    shouldCreateFromTemplateSelection(searchItem)
                      ? getTemplateCreatedItemLabel(searchItem)
                      : searchItem.label +
                      (dupplicateCurrent
                        ? ' [copie]'
                        : '')
                  );
                  setSearchedItemsList([]);
                  if (
                    mode !== 'folder' ||
                    shouldCreateFromTemplateSelection(searchItem)
                  ) {
                    setDupplicateCurrent(false);
                  }
                }}
              >
                <div className='flex-1'>
                  {searchItem.label}
                  {shouldCreateFromTemplateSelection(searchItem) && (
                    <span className='ml-05 text-xs uppercase text-secondary'>
                      template
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {searchedItem &&
        mode !== 'folder' &&
        !shouldCreateFromTemplateSelection(searchedItem) && (
        <div
          className='flex justify-center items-center gap-05 cursor-pointer'
          onClick={() => setDupplicateCurrent(!dupplicateCurrent)}
        >
          {dupplicateCurrent ? <FaCheck /> : <FaTimes />} Dupliquer{' '}
          {searchedItem.label}
        </div>
        )}
      <div className='w-full flex justify-center mt-1'>
        <BtnSimple
          className='flex gap-1 items-center'
          disabled={newItemLabel === ''}
          onClick={() => handleCreate()}
        >
          <FaPlus />
          {shouldCreateFromTemplateSelection(searchedItem)
            ? `Créer "${newItemLabel || getTemplateCreatedItemLabel(searchedItem)}" depuis le template "${searchedItem.label}"`
            : searchedItem
            ? `Créer un lien entre ${item?.label} et "${searchedItem.label}${
                dupplicateCurrent ? ' [copie]' : ''
              }"`
            : `Créer un lien entre ${item?.label} et "${newItemLabel}"`}
        </BtnSimple>
      </div>
    </ModalGlobal>
  );
};
